import type { EligibilityResult } from '../../../../api/user/eligibility.api';
import {
  unmetRequiredLabels,
  unmetReviewLabels,
} from '../../../../components/eligibility/eligibility-rules';

/** Lý do từ chối có sẵn, mã khớp máy chủ. "Lý do khác" bắt buộc ghi thêm. */
export const REJECT_REASONS: { code: string; label: string }[] = [
  { code: 'profile', label: 'Hồ sơ chưa đủ thông tin' },
  { code: 'activity', label: 'Kênh chưa đăng nội dung đều' },
  { code: 'direction', label: 'Nội dung chưa hợp với định hướng của Cab lúc này' },
  { code: 'identity', label: 'Thông tin xác minh danh tính chưa khớp' },
  { code: 'other', label: 'Lý do khác' },
];

export const REJECT_TEXT_MAX = 500;

/** Kiểm hộp từ chối trước khi gửi. Trả câu lỗi, hoặc null nếu gửi được. */
export const validateReject = (code: string | null | undefined, text: string | null | undefined): string | null => {
  if (!code) return 'Vui lòng chọn lý do từ chối.';
  if (!REJECT_REASONS.some((r) => r.code === code)) return 'Lý do từ chối không hợp lệ.';
  if (code === 'other' && !(text ?? '').trim()) return 'Vui lòng ghi rõ lý do khi chọn lý do khác.';
  return null;
};

/** Kiểm ô lý do duyệt ngoại lệ. */
export const validateExceptionNote = (note: string | null | undefined): string | null =>
  (note ?? '').trim() ? null : 'Vui lòng ghi lý do duyệt ngoại lệ.';

export type ApprovalPlan =
  | { kind: 'direct' }
  | { kind: 'blocked'; message: string }
  | { kind: 'needNote'; labels: string[] };

/**
 * Bấm duyệt thì làm gì, theo danh sách điều kiện admin vừa tải:
 * đã duyệt từ trước (đã mở donate / đã là creator) thì duyệt thẳng như máy chủ;
 * thiếu mục bắt buộc thì chặn; thiếu mục xét duyệt thì phải ghi lý do ngoại lệ.
 */
export const planApproval = (result: EligibilityResult | null | undefined): ApprovalPlan => {
  if (!result) return { kind: 'direct' };
  if (result.request?.status === 'approved') return { kind: 'direct' };
  // Máy chủ tính requiredMet đúng bằng "không mục bắt buộc nào chưa đạt", nên chỉ cần xem danh sách mục.
  const required = unmetRequiredLabels(result);
  if (required.length > 0) {
    return {
      kind: 'blocked',
      message: `Cab chưa thể duyệt vì tài khoản thiếu điều kiện bắt buộc: ${required.join(', ')}.`,
    };
  }
  const review = unmetReviewLabels(result);
  return review.length > 0 ? { kind: 'needNote', labels: review } : { kind: 'direct' };
};

export type AdminRequestStatus = 'pending' | 'approved' | 'rejected' | 'unknown';

/** Máy chủ trả trạng thái dạng chữ ("Pending"/"Approved"/"Reject"), dữ liệu cũ có thể là số (0/1/-1). */
export const normalizeRequestStatus = (value: unknown): AdminRequestStatus => {
  if (value === null || value === undefined) return 'unknown';
  const v = String(value).trim().toLowerCase();
  if (v === 'pending' || v === '0') return 'pending';
  if (v === 'approved' || v === '1') return 'approved';
  if (v === 'reject' || v === 'rejected' || v === '-1') return 'rejected';
  return 'unknown';
};

const STATUS_ORDER: Record<AdminRequestStatus, number> = {
  pending: 0,
  approved: 1,
  rejected: 2,
  unknown: 3,
};

export const compareRequestStatus = (a: unknown, b: unknown): number =>
  STATUS_ORDER[normalizeRequestStatus(a)] - STATUS_ORDER[normalizeRequestStatus(b)];

/** Lấy câu máy chủ trả khi thành công (một chuỗi JSON), không có thì dùng câu dự phòng. */
export const successText = (res: any, fallback: string): string => {
  const d = res?.data;
  if (typeof d === 'string' && d.trim()) return d.trim();
  if (typeof d?.data === 'string' && d.data.trim()) return d.data.trim();
  return fallback;
};
